/**
 * Struktur navigasi konsol (IA) — **vertikal, berkelompok**, sejalan dengan OSB Nexus.
 *
 * ⚠️ `label` adalah **kunci i18n**, bukan teks. Menaruh teks di sini membuat navigasi jadi
 * satu-satunya bagian layar yang tak ikut berpindah bahasa — dan itu justru yang paling sering
 * dilihat.
 *
 * ⛔ Yang belum ada layarnya TIDAK didaftarkan. Item mati yang menunggu diisi mengajari pengguna
 * bahwa sebagian menu memang tak bekerja; sesudah itu menu yang benar-benar rusak tak dilaporkan.
 */
import {
  ApiOutlined,
  ControlOutlined,
  CreditCardOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  ApartmentOutlined, AppstoreOutlined, BorderOutlined, DollarOutlined, GoldOutlined,
  IdcardOutlined, PercentageOutlined,
  PrinterOutlined, ShopOutlined, StopOutlined, TagOutlined,
  LineChartOutlined,
  BellOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

export interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
}

export interface NavGroup {
  readonly label?: string;
  readonly items: readonly NavItem[];
}

export const NAV: readonly NavGroup[] = [
  {
    label: 'nav.grpOutlet',
    items: [
      /* ⭐ Brand DULU, outlet sesudahnya — itu urutan pembuatannya yang sebenarnya: satu tenant
         boleh punya beberapa brand, dan tiap outlet wajib milik salah satunya. Menaruh outlet di
         atas membuat orang mencoba membuat outlet sebelum ada brand yang bisa dipilih. */
      { key: 'brands', label: 'brands.title', icon: <GoldOutlined /> },
      { key: 'outlets', label: 'outlets.title', icon: <ShopOutlined /> },
      /* ⭐ Lantai outlet (area · meja · perangkat) tepat SESUDAH outlet — itu urutan pengerjaannya:
         outlet dibuat, lalu lantainya disiapkan. Gerbang kesiapan menuntut keduanya. */
      { key: 'floor', label: 'floor.title', icon: <BorderOutlined /> },
    ],
  },
  {
    /*
     * ⭐⭐ **Laporan di ATAS master**, dan itu disengaja. Master ditata SEKALI saat onboarding;
     * laporan dibuka TIAP HARI oleh orang yang menutup laci. Menu diurutkan menurut frekuensi
     * pemakaian, bukan menurut urutan pembuatan data.
     */
    label: 'nav.grpReport',
    items: [
      { key: 'reports', label: 'report.tab', icon: <LineChartOutlined /> },
      /* ⭐ Notifikasi duduk di sebelah laporan, bukan di master: ia dibaca pada ritme yang sama
         (harian, oleh orang yang sama), dan yang dicarinya juga sama — apa yang terjadi semalam. */
      { key: 'notifications', label: 'notify.tab', icon: <BellOutlined /> },
    ],
  },
  {
    label: 'nav.grpMaster',
    items: [
      /* ⭐ Struktur (departemen & kategori) DIPISAH dari produk (Imron, 2026-08-02): keduanya
         dikerjakan orang berbeda pada waktu berbeda — struktur ditata sekali saat onboarding,
         produk disunting tiap minggu. Satu halaman berisi keduanya membuat yang sering dipakai
         harus melewati yang jarang diubah. */
      { key: 'structure', label: 'structure.tab', icon: <ApartmentOutlined /> },
      { key: 'products', label: 'products.tab', icon: <AppstoreOutlined /> },
      /* ⭐ Grup harga duduk di sebelah produk: ia menjawab "harga ini untuk siapa", dan itu
         pertanyaan yang muncul saat harga disunting — bukan saat outlet dibuat. */
      { key: 'price-groups', label: 'priceGroup.title', icon: <DollarOutlined /> },
      /* ⭐ Grup modifier dipakai BANYAK produk ⇒ layar tersendiri, sama seperti Struktur. Memilih
         grup mana yang berlaku untuk sebuah produk tetap di laci produk. */
      { key: 'modifiers', label: 'modifier.tab', icon: <ControlOutlined /> },
      /* ⭐ Kelas fiskal produk + batas kanal. Bukan tarif — tarif tak bisa disunting sama sekali. */
      { key: 'taxes', label: 'tax.tab', icon: <PercentageOutlined /> },
      /* ⭐ Keduanya keputusan per OUTLET, bukan sifat produk. */
      { key: 'availability', label: 'availability.tab', icon: <StopOutlined /> },
      { key: 'promo', label: 'promo.tab', icon: <TagOutlined /> },
      { key: 'routing', label: 'routing.tab', icon: <PrinterOutlined /> },
      /* ⛔⛔ Metode bayar adalah PEMBLOKIR KESIAPAN: tanpa satu pun, outlet tak bisa berjualan
         sama sekali. Ditaruh di master karena ia ditata sekali saat onboarding, seperti pajak. */
      { key: 'payments', label: 'payments.tab', icon: <CreditCardOutlined /> },
      /* ⭐ Stok duduk di master karena resep & bahan memang master — tapi tab-nya juga memuat
         pergerakan harian, jadi ia satu-satunya layar master yang dibuka tiap minggu. */
      { key: 'stock', label: 'stock.tab', icon: <DatabaseOutlined /> },
      /* ⭐ Kepala & kaki struk — satu-satunya bagian sistem yang DIBACA TAMU. Ditaruh sesudah
         metode bayar karena keduanya disetel sekali saat onboarding, bukan tiap hari. */
      { key: 'receipt', label: 'receipt.tab', icon: <PrinterOutlined /> },
      /* ⛔ HANYA INFO: paket, hak pakai, masa berlaku. Provisioning tetap milik OSB — memberi
         tenant tombolnya membuat gerbang bergantung pada KEADAAN, bukan pada scope. */
      { key: 'subscription', label: 'subscription.tab', icon: <SafetyCertificateOutlined /> },
      /* ⛔⛔ Pemblokir onboarding kedua: tanpa staf ber-PIN, tak ada satu pun kasir yang bisa
         login — dan izin sampai ke orang lewat JABATAN, bukan lewat centang per orang. */
      { key: 'roles', label: 'roles.title', icon: <IdcardOutlined /> },
      /* ⚠️ Peran SEBELUM staf: tanpa peran, layar staf tak punya apa pun untuk dipilih. */
      { key: 'staff', label: 'staff.tab', icon: <TeamOutlined /> },
      /* ⭐⭐ Kunci M2M milik tenant (skrip laporan, BI, server MCP). Ditaruh PALING BAWAH dengan
         sengaja: ia jarang disentuh, dan yang menyentuhnya sedang memberi akses ke pihak luar —
         bukan menyunting menu. */
      /* ⭐⭐ Pengguna KONSOL — beda populasi dengan Staf (yang itu PIN & lantai, ini sandi & HQ).
         ⚠️ Bersebelahan dengan Staf dengan sengaja: pemilik yang mencari akuntannya di layar Staf
         harus melihat tab ini tanpa mencarinya. */
      { key: 'console-users', label: 'consoleUsers.tab', icon: <SafetyCertificateOutlined /> },
      { key: 'integrations', label: 'integration.tab', icon: <ApiOutlined /> },
    ],
  },
];
